import useSWR from "swr";
import useSWRMutation, { SWRMutationConfiguration } from "swr/mutation";
import { useRef, useEffect, useState, useCallback } from "react";
import equal from "fast-deep-equal/react";

import config from "config";
import { useFirebase } from "../firebase";

export class NetworkError extends Error {
  statusCode: number;
  responseData: any;

  constructor(statusCode: number, responseData?: any) {
    super(`Network Error: ${statusCode}`);
    this.statusCode = statusCode;
    this.responseData = responseData;
  }
}

// Define the expected type for the getIdToken function parameter
type GetIdTokenFn = () => Promise<string | null> | undefined;

// Function that returns undefined, matching the type signature for public calls
const undefinedGetIdTokenFn: GetIdTokenFn = () => undefined;

// Update api to accept getIdToken function and EXPORT it
export function api(requestOptions = {}, getIdToken: GetIdTokenFn) {
  return async (url: string, options?: { arg: any }) => {
    const baseHeaders = (requestOptions as RequestInit).headers || {};
    let dynamicHeaders: HeadersInit = {};

    if (getIdToken) {
      try {
        const token = await getIdToken();
        if (token) {
          dynamicHeaders["Authorization"] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error("Error getting Firebase ID token:", error);
      }
    }

    const fetchOptions: RequestInit = {
      headers: { ...baseHeaders },
      credentials: "include",
      ...(requestOptions as RequestInit),
      ...(options ? { body: JSON.stringify(options.arg) } : {}),
    };

    fetchOptions.headers = {
      ...baseHeaders,
      ...dynamicHeaders,
      ...(requestOptions as RequestInit).headers,
    };

    // Clean up potential undefined/null headers
    Object.keys(fetchOptions.headers).forEach((_key) => {
      const key = _key as keyof HeadersInit;
      if (
        fetchOptions.headers![key] === undefined ||
        fetchOptions.headers![key] === null
      ) {
        delete fetchOptions.headers![key];
      }
    });

    const response = await fetch(
      `${config.apiEndpoint}/api/v1${url}`,
      fetchOptions,
    );

    const contentType = response.headers.get("content-type");

    if (contentType && contentType.indexOf("application/json") >= 0) {
      const json = await response.json().catch(() => response);

      if (!response.ok) {
        throw new NetworkError(response.status, json);
      }

      return json;
    }

    if (!response.ok) {
      throw new NetworkError(response.status);
    }

    return response;
  };
}

// Pass the undefined function for getIdToken
export function fetchPublicAPI<T = any>(url: string, options = {}) {
  return api(options, undefinedGetIdTokenFn)(url) as Promise<T>;
}

type FetchPublicAPIArgs = {
  url: string;
  options?: RequestInit;
  data?: unknown;
};

// Pass the undefined function for getIdToken
export function useFetchPublicAPI<T>({
  url,
  options,
  data,
}: FetchPublicAPIArgs) {
  const [response, setResponse] = useState<T | null>(null);
  const [error, setError] = useState<NetworkError | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const urlRef = useRef(url);
  const optionsRef = useRef(options);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Pass the undefined function for getIdToken
        const response = await api(optionsRef.current, undefinedGetIdTokenFn)(
          urlRef.current,
          data ? { arg: data } : undefined,
        );
        if (isMounted) {
          setResponse(response);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError(err as NetworkError);
          setResponse(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  return { data: response, error, isLoading };
}

export function useGetIdToken() {
  const { user } = useFirebase();
  const deps = [user];
  const ref = useRef(deps);

  if (!equal(deps, ref.current)) {
    ref.current = deps;
  }

  return useCallback(async () => {
    try {
      return user ? await user.getIdToken() : null;
    } catch (error) {
      console.error("Error in getIdToken callback:", error);
      return null;
    }
  }, ref.current);
}

export function useGET(route: any, requestOptions?: any, swrOptions?: any) {
  const getIdToken = useGetIdToken();
  const response = useSWR(route, api(requestOptions, getIdToken), swrOptions);

  return response;
}

export function usePOST<TData = any, TError = NetworkError, TArg = any>(
  route: string,
  swrOptions?: SWRMutationConfiguration<TData, TError, string, TArg>,
) {
  const getIdToken = useGetIdToken();
  const response = useSWRMutation<TData, TError, string, TArg>(
    route,
    api(
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      },
      getIdToken,
    ),
    swrOptions,
  );

  return response;
}
